import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import ScrollReveal from "../components/ScrollReveal";
import StatsSection from "../components/StatsSection";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INVESTOR_VIDEO =
  "https://customer-assets.emergentagent.com/job_maclear-holdings/artifacts/smgzqdw2_WhatsApp%20Video%202026-05-19%20at%207.45.29%20PM.mp4";

const INTEREST_AREAS = [
  "Smart City Infrastructure",
  "Tourism & Lifestyle",
  "Logistics & Aviation",
  "Agriculture & Industry",
  "Renewable Energy",
  "Real Estate / Mixed-Use",
  "Strategic Partnership",
  "Other",
];

const RANGES = [
  "Under $1M",
  "$1M – $10M",
  "$10M – $50M",
  "$50M – $250M",
  "$250M+",
];

const REASONS = [
  { title: "Africa's Next Gateway", body: "Within a 3-hour flight of 23 nations and a 577M+ regional market." },
  { title: "Government-Backed SEZ", body: "A special economic zone designation with structured regulatory certainty." },
  { title: "Multi-Sector Returns", body: "Exposure spanning tourism, logistics, energy, industry and real estate." },
  { title: "Sustainability By Design", body: "Renewable energy and smart planning aligned with the UN SDGs." },
  { title: "World-Class Partners", body: "Global development firms, technical experts and strategic investors already engaged." },
  { title: "Job & Skills Impact", body: "Over 49,000 local job opportunities and a platform for regional skills transfer." },
];

export default function InvestorRelations() {
  const [form, setForm] = useState({
    full_name: "",
    email: "",
    organization: "",
    phone: "",
    country: "",
    interest_area: INTEREST_AREAS[0],
    investment_range: RANGES[1],
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (e) =>
    setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/investor-inquiry`, form);
      toast.success("Inquiry received. Our investor relations team will be in touch.");
      setDone(true);
      setForm((f) => ({
        ...f,
        full_name: "",
        email: "",
        organization: "",
        phone: "",
        country: "",
        message: "",
      }));
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="page-investors" className="pt-32 md:pt-40 bg-[#FCFCFA]">
      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pb-24">
        <div className="grid md:grid-cols-12 gap-10 md:gap-12 items-center">
          <ScrollReveal className="md:col-span-7 order-1">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Investor Relations</span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-7xl leading-[1.02] text-[#1A1D20]">
              A USD 3.5B opportunity, engineered for global capital.
            </h1>
            <p className="mt-8 text-[#5C636A] text-[16px] md:text-[18px] leading-relaxed max-w-2xl">
              The Cape Maclear Special Economic Zone is one of Africa's most
              ambitious and transformative projects — and one of the most
              strategically positioned. We welcome aligned investors and
              institutional partners to join us in shaping this landmark
              development.
            </p>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-5 order-2 md:order-2">
            <div className="relative border border-[#E5E5DF] bg-[#1A1D20] overflow-hidden shadow-sm">
              <div className="absolute top-4 left-4 z-10 inline-flex items-center gap-2 px-3 py-1.5 bg-[#1A1D20]/70 backdrop-blur-sm border border-[#D4AF37]/40">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping" />
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#D4AF37]" />
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-semibold">
                  Project Film
                </span>
              </div>
              <video
                src={INVESTOR_VIDEO}
                data-testid="investor-hero-video"
                className="w-full h-full block aspect-video object-cover bg-[#1A1D20]"
                controls
                playsInline
                preload="metadata"
                poster=""
              >
                Sorry, your browser does not support embedded video.
              </video>
            </div>
            <p className="mt-4 text-[11px] uppercase tracking-[0.22em] text-[#5C636A]">
              Cape Maclear SEZ · Investor Brief
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* STO Coming Soon Banner */}
      <section
        data-testid="sto-banner"
        className="relative bg-[#1A1D20] text-[#FCFCFA] overflow-hidden"
      >
        {/* Decorative grid + glow */}
        <div
          className="absolute inset-0 opacity-[0.08] pointer-events-none"
          style={{
            backgroundImage:
              "linear-gradient(#D4AF37 1px, transparent 1px), linear-gradient(90deg, #D4AF37 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
        <div
          className="absolute -right-40 -top-40 w-[520px] h-[520px] rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(closest-side, rgba(212,175,55,0.35), transparent 70%)",
          }}
        />

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28 grid md:grid-cols-12 gap-12 items-center">
          <ScrollReveal className="md:col-span-7">
            <div className="inline-flex items-center gap-3 px-4 py-2 border border-[#D4AF37] text-[#D4AF37] mb-8">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-[#D4AF37]" />
              </span>
              <span className="text-[11px] uppercase tracking-[0.25em] font-semibold">
                Coming Soon
              </span>
            </div>
            <p className="overline mb-4">Blockchain · Security Token Offering</p>
            <h2 className="font-serif-display text-4xl md:text-6xl leading-[1.02]">
              Cape Maclear STO
              <span className="block text-[#D4AF37]">First Phase · USD 120M+</span>
            </h2>
            <p className="mt-8 text-white/80 text-[15px] md:text-[17px] leading-relaxed max-w-2xl">
              An exclusive early-participation opportunity for qualified
              investors — gain tokenized, blockchain-secured exposure to the
              first <span className="text-white font-medium">USD 120 million+</span> phase of the Cape Maclear
              Special Economic Zone, an asset whose <span className="text-white font-medium">current value already exceeds USD 400 million+</span>.
              A Security Token Offering structured for transparency, liquidity
              and regulatory-grade compliance.
            </p>

            <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 max-w-3xl">
              <div className="bg-[#1A1D20] p-5">
                <p className="font-serif-display text-3xl md:text-4xl text-[#D4AF37] leading-none">
                  38%<span className="text-xl">+</span>
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                  Target Return
                </p>
              </div>
              <div className="bg-[#1A1D20] p-5">
                <p className="font-serif-display text-3xl md:text-4xl text-[#D4AF37] leading-none">
                  6<span className="text-xl"> mo</span>
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                  Target Horizon
                </p>
              </div>
              <div className="bg-[#1A1D20] p-5">
                <p className="font-serif-display text-3xl md:text-4xl text-[#D4AF37] leading-none">
                  $120M<span className="text-xl">+</span>
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                  Phase-One Raise
                </p>
              </div>
              <div className="bg-[#1A1D20] p-5">
                <p className="font-serif-display text-3xl md:text-4xl text-[#D4AF37] leading-none">
                  $400M<span className="text-xl">+</span>
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.22em] text-white/70">
                  Current Value
                </p>
              </div>
            </div>

            <p className="mt-8 text-white/60 text-[12px] leading-relaxed max-w-2xl italic">
              Indicative figures. Full offering terms, jurisdictions of
              eligibility, risk disclosures and regulatory registrations will be
              published in the official STO prospectus ahead of launch. This
              banner does not constitute an offer or solicitation in any
              jurisdiction where such offer would be unlawful.
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <a
                href="#investor-form-section"
                data-testid="sto-cta-register"
                className="inline-flex items-center bg-[#D4AF37] text-[#1A1D20] hover:bg-white transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
                onClick={(e) => {
                  e.preventDefault();
                  document
                    .querySelector('[data-testid="investor-form-section"]')
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                Register Early Interest
              </a>
              <a
                href="https://10dmedia.online/books/hvsw/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="sto-cta-profile"
                className="inline-flex items-center border border-white/60 text-white hover:bg-white hover:text-[#1A1D20] transition-colors duration-300 px-8 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold"
              >
                View Company Profile
              </a>
            </div>
          </ScrollReveal>

          <ScrollReveal delay={0.15} className="md:col-span-5">
            <div className="relative bg-gradient-to-br from-[#2C3035] to-[#1A1D20] border border-[#D4AF37]/40 p-10 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <span className="text-[11px] uppercase tracking-[0.25em] text-white/60">
                  Token Summary
                </span>
                <span className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37]">
                  Preview
                </span>
              </div>
              <dl className="space-y-5">
                {[
                  ["Asset", "Cape Maclear SEZ — Phase I"],
                  ["Instrument", "Security Token (blockchain-issued)"],
                  ["Phase-One Raise", "USD 120,000,000+"],
                  ["Current Valuation", "USD 400,000,000+"],
                  ["Target Return", "38%+ in ~6 months"],
                  ["Allocation", "Priority to early participants"],
                  ["Status", "Pre-launch · Whitelist Open"],
                ].map(([k, v]) => (
                  <div
                    key={k}
                    className="flex items-start justify-between gap-6 pb-4 border-b border-white/10 last:border-b-0 last:pb-0"
                  >
                    <dt className="text-[11px] uppercase tracking-[0.22em] text-white/60 shrink-0">
                      {k}
                    </dt>
                    <dd className="text-right text-[13px] text-white/90 font-medium">
                      {v}
                    </dd>
                  </div>
                ))}
              </dl>
            </div>
          </ScrollReveal>
        </div>
      </section>

      <StatsSection />

      {/* Why invest */}
      <section className="py-24 md:py-32" data-testid="why-invest">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid md:grid-cols-12 gap-10 mb-16">
            <div className="md:col-span-5">
              <div className="flex items-center gap-4 mb-6">
                <span className="hairline-gold" />
                <span className="overline">Why Invest</span>
              </div>
              <h2 className="font-serif-display text-4xl md:text-5xl leading-tight text-[#1A1D20]">
                Six reasons the smartest capital is already moving.
              </h2>
            </div>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#E5E5DF]">
            {REASONS.map((r, i) => (
              <ScrollReveal key={r.title} delay={i * 0.05} className="bg-[#FCFCFA]">
                <div className="p-10 h-full">
                  <CheckCircle2 size={24} strokeWidth={1.5} className="text-[#D4AF37]" />
                  <p className="mt-6 font-serif-display text-2xl text-[#1A1D20]">{r.title}</p>
                  <p className="mt-3 text-[14px] leading-relaxed text-[#5C636A]">{r.body}</p>
                </div>
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* Inquiry form */}
      <section id="investor-form-section" className="bg-[#1A1D20] text-[#FCFCFA] py-24 md:py-32" data-testid="investor-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <div className="flex items-center gap-4 mb-6">
              <span className="hairline-gold" />
              <span className="overline">Investor Inquiry</span>
            </div>
            <h2 className="font-serif-display text-4xl md:text-5xl leading-tight">
              Start a confidential conversation with our investor relations team.
            </h2>
            <p className="mt-8 text-white/75 text-[15px] leading-relaxed">
              Share a little about your organization and area of interest.
              Our team will respond within two business days.
            </p>
          </div>

          <div className="md:col-span-7">
            {done ? (
              <div
                data-testid="investor-success"
                className="border border-[#D4AF37] p-10"
              >
                <CheckCircle2 size={28} className="text-[#D4AF37]" strokeWidth={1.5} />
                <p className="mt-6 font-serif-display text-3xl">Thank you.</p>
                <p className="mt-3 text-white/75 text-[15px] leading-relaxed">
                  Your inquiry has been received. A member of the Cape Maclear
                  Holdings investor relations team will be in touch shortly.
                </p>
                <button
                  onClick={() => setDone(false)}
                  data-testid="investor-reset"
                  className="mt-8 text-[12px] uppercase tracking-[0.2em] text-[#D4AF37]"
                >
                  Submit another inquiry →
                </button>
              </div>
            ) : (
              <form
                onSubmit={onSubmit}
                data-testid="investor-form"
                className="grid grid-cols-1 md:grid-cols-2 gap-8"
              >
                <FieldDark label="Full Name" required>
                  <input
                    required
                    name="full_name"
                    value={form.full_name}
                    onChange={onChange}
                    data-testid="investor-name"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40"
                    placeholder="Your name"
                  />
                </FieldDark>
                <FieldDark label="Email" required>
                  <input
                    required
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={onChange}
                    data-testid="investor-email"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40"
                    placeholder="you@company.com"
                  />
                </FieldDark>
                <FieldDark label="Organization">
                  <input
                    name="organization"
                    value={form.organization}
                    onChange={onChange}
                    data-testid="investor-org"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40"
                    placeholder="Company / Fund"
                  />
                </FieldDark>
                <FieldDark label="Country">
                  <input
                    name="country"
                    value={form.country}
                    onChange={onChange}
                    data-testid="investor-country"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40"
                    placeholder="Country"
                  />
                </FieldDark>
                <FieldDark label="Phone">
                  <input
                    name="phone"
                    value={form.phone}
                    onChange={onChange}
                    data-testid="investor-phone"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40"
                    placeholder="+1 ..."
                  />
                </FieldDark>
                <FieldDark label="Investment Range">
                  <select
                    name="investment_range"
                    value={form.investment_range}
                    onChange={onChange}
                    data-testid="investor-range"
                    className="w-full bg-[#1A1D20] border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px]"
                  >
                    {RANGES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </FieldDark>
                <FieldDark label="Interest Area" required className="md:col-span-2">
                  <select
                    required
                    name="interest_area"
                    value={form.interest_area}
                    onChange={onChange}
                    data-testid="investor-interest"
                    className="w-full bg-[#1A1D20] border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px]"
                  >
                    {INTEREST_AREAS.map((a) => (
                      <option key={a} value={a}>{a}</option>
                    ))}
                  </select>
                </FieldDark>
                <FieldDark label="Message" required className="md:col-span-2">
                  <textarea
                    required
                    name="message"
                    rows={5}
                    minLength={5}
                    value={form.message}
                    onChange={onChange}
                    data-testid="investor-message"
                    className="w-full bg-transparent border-b border-white/30 focus:border-[#D4AF37] outline-none py-3 text-[15px] placeholder-white/40 resize-none"
                    placeholder="Tell us about your interest..."
                  />
                </FieldDark>

                <div className="md:col-span-2 pt-4">
                  <button
                    type="submit"
                    disabled={submitting}
                    data-testid="investor-submit"
                    className="inline-flex items-center bg-[#D4AF37] text-[#1A1D20] hover:bg-white transition-colors duration-300 px-10 py-4 uppercase tracking-[0.18em] text-[12px] font-semibold disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? "Sending..." : "Submit Inquiry"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </main>
  );
}

function FieldDark({ label, required, className = "", children }) {
  return (
    <label className={`block ${className}`}>
      <span className="block text-[10px] uppercase tracking-[0.22em] text-white/60 mb-2">
        {label}{required && <span className="text-[#D4AF37]"> *</span>}
      </span>
      {children}
    </label>
  );
}
